import { useSelector } from 'react-redux';
import {
  selectCurrency,
  selectOfferCategory,
  selectOfferId,
  selectInputAddress,
  selectPhone,
  selectPrice,
} from '../../../selectors/offer';
import { selectUserEmail, selectUserId } from '../../../selectors/user';
import { ECategory, EDealType } from '../../../repositories/demand/v2/create-message-for-addform';
import { TExecuteCallback, useAsync } from '../../../hooks/useAsync';
import { createMessageForAddForm } from '../../../services/createMessageForAddForm';
import { ApplicationContext } from '../../ApplicationContext';
import { useContext, useEffect } from 'react';
import { trackClickButtonDemandTrap, trackShowDemandTrap, trackSuccessCreatedMessage } from '../tracking';
import { IAdaptiveSettings } from '../../../types/adaptiveSettings';
import { getAnalyticsSiteType } from '../../../utils';

export const useCreateMessage = (
  dealType: string,
): [
  status: { isLoading: boolean; isSuccess: boolean; isError: boolean },
  executeCreateMessage: TExecuteCallback<void>,
] => {
  const context = useContext(ApplicationContext);
  const { deviceType } = context.config.get<IAdaptiveSettings>('adaptiveSettings') || {};

  const siteType = getAnalyticsSiteType(deviceType);

  const address = useSelector(selectInputAddress);
  const category = useSelector(selectOfferCategory);
  const email = useSelector(selectUserEmail);
  const userId = useSelector(selectUserId);
  const phone = useSelector(selectPhone);
  const price = useSelector(selectPrice);
  const currency = useSelector(selectCurrency);
  const offerId = useSelector(selectOfferId);

  useEffect(() => {
    trackShowDemandTrap({ dealType, category, offerId, userId, siteType });
  }, []);

  const parameters = {
    address,
    category: category as unknown as ECategory,
    dealType: dealType as EDealType,
    email,
    phone,
    price,
    currency,
    source: 'AddNewOffer_successPage',
  };

  const createMessage = async () => {
    trackClickButtonDemandTrap({ dealType, category, offerId, userId, siteType });
    await createMessageForAddForm({ context, parameters });
    trackSuccessCreatedMessage({ dealType, category, offerId, userId, siteType });
  };

  const [{ status }, executeCreateMessage] = useAsync<void>(createMessage);

  const isLoading = status === 'pending';
  const isSuccess = status === 'success';
  const isError = status === 'error';

  return [{ isLoading, isSuccess, isError }, executeCreateMessage];
};
